import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import './Statistics.css';

interface StatisticsData {
  totalSubmissions: number;
  totalUsers: number;
  recentSubmissions: number;
  topAdvertisers: Array<{ _id: string; count: number }>;
  topUsers: Array<{ 
    _id: string; 
    count: number; 
    name: string; 
    email: string; 
    department: string 
  }>;
  channelStats: {
    totalSubmissions: number;
    facebookCount: number;
    linkedinCount: number;
    andreKanalerCount: number;
  };
  monthlyTrends: Array<{ date: string; count: number }>;
  integrationStats: {
    totalSubmissions: number;
    emailsSent: number;
    asanaTasksCreated: number;
    pdfsGenerated: number;
  };
}

const Statistics: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<StatisticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.role === 'administrator') {
      fetchStatistics();
    }
  }, [user]);

  // Only allow admin users
  if (user?.role !== 'administrator') {
    return (
      <div className="statistics-page">
        <Header />
        <div className="statistics-content">
          <div className="container">
            <h1>Ingen tilgang</h1>
            <p>Du har ikke tilgang til statistikk. Kun administratorer kan se denne siden.</p>
          </div>
        </div>
      </div>
    );
  }

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/forms/submissions/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        throw new Error('Failed to fetch statistics');
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
      setError('Kunne ikke laste statistikk');
    } finally {
      setLoading(false);
    }
  };

  const formatMonth = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('no-NO', { year: 'numeric', month: 'short' });
  };

  const calculatePercentage = (value: number, total: number) => {
    return total > 0 ? ((value / total) * 100).toFixed(1) : '0';
  };

  if (loading) {
    return (
      <div className="statistics-page">
        <Header />
        <div className="statistics-content">
          <div className="container">
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Laster statistikk...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="statistics-page">
        <Header />
        <div className="statistics-content">
          <div className="container">
            <div className="error-message">
              <h2>Feil ved lasting av statistikk</h2>
              <p>{error}</p>
              <button className="btn btn-primary" onClick={fetchStatistics}>
                Prøv igjen
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="statistics-page">
      <Header />
      <div className="statistics-content">
        <div className="container">
          <div className="statistics-header">
            <h1>📊 Statistikk Dashboard</h1>
            <p>Oversikt over salgsskjema og systemaktivitet</p>
          </div>

          {/* Overview Cards */}
          <div className="stats-overview">
            <div className="stats-card primary">
              <div className="stats-icon">📝</div>
              <div className="stats-info">
                <h3>{stats.totalSubmissions}</h3>
                <p>Totale innsendinger</p>
              </div>
            </div>
            
            <div className="stats-card success">
              <div className="stats-icon">👥</div>
              <div className="stats-info">
                <h3>{stats.totalUsers}</h3>
                <p>Aktive brukere</p>
              </div>
            </div>
            
            <div className="stats-card warning">
              <div className="stats-icon">🕒</div>
              <div className="stats-info">
                <h3>{stats.recentSubmissions}</h3>
                <p>Siste 30 dager</p>
              </div>
            </div>
            
            <div className="stats-card info">
              <div className="stats-icon">✅</div>
              <div className="stats-info">
                <h3>{calculatePercentage(stats.integrationStats.emailsSent, stats.integrationStats.totalSubmissions)}%</h3>
                <p>E-post sendt</p>
              </div>
            </div>
          </div>

          {/* Channel Statistics */}
          <div className="stats-section">
            <h2>📺 Kanalstatistikk</h2>
            <div className="channel-stats">
              <div className="channel-card facebook">
                <div className="channel-header">
                  <div className="channel-icon">📘</div>
                  <h3>Facebook / Instagram</h3>
                </div>
                <div className="channel-metrics">
                  <div className="metric-large">{stats.channelStats.facebookCount}</div>
                  <div className="metric-small">
                    {calculatePercentage(stats.channelStats.facebookCount, stats.channelStats.totalSubmissions)}% av alle kampanjer
                  </div>
                </div>
              </div>

              <div className="channel-card linkedin">
                <div className="channel-header">
                  <div className="channel-icon">💼</div>
                  <h3>LinkedIn</h3>
                </div>
                <div className="channel-metrics">
                  <div className="metric-large">{stats.channelStats.linkedinCount}</div>
                  <div className="metric-small">
                    {calculatePercentage(stats.channelStats.linkedinCount, stats.channelStats.totalSubmissions)}% av alle kampanjer
                  </div>
                </div>
              </div>

              <div className="channel-card other">
                <div className="channel-header">
                  <div className="channel-icon">📊</div>
                  <h3>Andre kanaler</h3>
                </div>
                <div className="channel-metrics">
                  <div className="metric-large">{stats.channelStats.andreKanalerCount}</div>
                  <div className="metric-small">
                    {calculatePercentage(stats.channelStats.andreKanalerCount, stats.channelStats.totalSubmissions)}% av alle kampanjer
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="stats-grid">
            {/* Top Advertisers */}
            <div className="stats-section">
              <h2>🏆 Topp 5 Annonsører</h2>
              <div className="ranking-list">
                {stats.topAdvertisers.slice(0, 5).map((advertiser, index) => (
                  <div key={advertiser._id} className="ranking-item">
                    <div className="ranking-position">
                      <span className={`position-badge position-${index + 1}`}>
                        {index + 1}
                      </span>
                    </div>
                    <div className="ranking-info">
                      <h4>{advertiser._id}</h4>
                      <p>{advertiser.count} kampanjer</p>
                    </div>
                    <div className="ranking-bar">
                      <div 
                        className="ranking-progress"
                        style={{ 
                          width: `${(advertiser.count / stats.topAdvertisers[0].count) * 100}%` 
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Users */}
            <div className="stats-section">
              <h2>🌟 Mest Aktive Brukere</h2>
              <div className="user-list">
                {stats.topUsers.slice(0, 5).map((user, index) => (
                  <div key={user._id} className="user-item">
                    <div className="user-avatar">
                      {user.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </div>
                    <div className="user-info">
                      <h4>{user.name}</h4>
                      <p>{user.department || 'Ukjent avdeling'}</p>
                    </div>
                    <div className="user-stats">
                      <span className="submission-count">{user.count}</span>
                      <span className="submission-label">innsendinger</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Activity Summary */}
          <div className="stats-section">
            <h2>📊 Aktivitetssammendrag</h2>
            <div className="activity-summary">
              <div className="activity-item">
                <div className="activity-icon">📈</div>
                <div className="activity-content">
                  <h4>Siste måned</h4>
                  <p>{stats.recentSubmissions} nye innsendinger</p>
                </div>
              </div>
              <div className="activity-item">
                <div className="activity-icon">⚡</div>
                <div className="activity-content">
                  <h4>Gjennomsnitt per dag</h4>
                  <p>{Math.round(stats.recentSubmissions / 30)} innsendinger</p>
                </div>
              </div>
              <div className="activity-item">
                <div className="activity-icon">🎯</div>
                <div className="activity-content">
                  <h4>Mest populære kanaler</h4>
                  <p>
                    {stats.channelStats.facebookCount >= stats.channelStats.linkedinCount && 
                     stats.channelStats.facebookCount >= stats.channelStats.andreKanalerCount 
                      ? 'Facebook/Instagram' 
                      : stats.channelStats.linkedinCount >= stats.channelStats.andreKanalerCount 
                        ? 'LinkedIn' 
                        : 'Andre kanaler'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Integration Status */}
          <div className="stats-section">
            <h2>🔗 Integrasjonsstatus</h2>
            <div className="integration-stats">
              <div className="integration-item">
                <div className="integration-label">E-post sendt</div>
                <div className="integration-bar">
                  <div 
                    className="integration-progress email"
                    style={{ 
                      width: `${calculatePercentage(stats.integrationStats.emailsSent, stats.integrationStats.totalSubmissions)}%` 
                    }}
                  />
                </div>
                <div className="integration-value">
                  {stats.integrationStats.emailsSent} / {stats.integrationStats.totalSubmissions}
                </div>
              </div>

              <div className="integration-item">
                <div className="integration-label">Asana oppgaver opprettet</div>
                <div className="integration-bar">
                  <div 
                    className="integration-progress asana"
                    style={{ 
                      width: `${calculatePercentage(stats.integrationStats.asanaTasksCreated, stats.integrationStats.totalSubmissions)}%` 
                    }}
                  />
                </div>
                <div className="integration-value">
                  {stats.integrationStats.asanaTasksCreated} / {stats.integrationStats.totalSubmissions}
                </div>
              </div>

              <div className="integration-item">
                <div className="integration-label">PDF generert</div>
                <div className="integration-bar">
                  <div 
                    className="integration-progress pdf"
                    style={{ 
                      width: `${calculatePercentage(stats.integrationStats.pdfsGenerated, stats.integrationStats.totalSubmissions)}%` 
                    }}
                  />
                </div>
                <div className="integration-value">
                  {stats.integrationStats.pdfsGenerated} / {stats.integrationStats.totalSubmissions}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statistics;